import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import {
    catchError,
    filter,
    map,
    MonoTypeOperatorFunction,
    Observable,
    of,
    switchMap,
    tap,
    timer,
    take,
} from 'rxjs'
import { jobActions } from './job.actions'
import { caseActions } from '../case/case.actions'
import { JobApiService } from '../../shared/services/job-api.service'
import { userActions } from '../user/user.actions'
import { Job, JobStatus, PipelineType } from '../../shared/model/jobs'
import { JobUpdateDialogService } from '../../shared/services/job-update-dialog.service'
import { Store } from '@ngrx/store'
import { selectJobs } from './job.selectors'
import { selectCaseId } from '../case/case.selectors'
import { HttpErrorResponse } from '@angular/common/http'
import { DatasetApiService } from '../../shared/services/dataset-api.service'

@Injectable()
export class JobEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly jobApiService: JobApiService,
        private readonly jobUpdateDialogService: JobUpdateDialogService,
        private readonly datasetApi: DatasetApiService
    ) {}

    private isPollingActive = false

    loadJobsInitially$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            filter(({ caseId, shared }) => !!caseId && !shared),
            tap(() => (this.isPollingActive = true)),
            this.loadJobs()
        )
    })

    reloadJobs$ = createEffect(() => {
        return this.actions$.pipe(ofType(jobActions.reloadJobs), this.loadJobs())
    })

    continuePollingJobs$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(jobActions.loadingJobsSucceeded, jobActions.loadingJobsFailed),
            filter(() => this.isPollingActive),
            switchMap(_ =>
                timer(10000).pipe(
                    switchMap(() =>
                        this.jobApiService.getJobs().pipe(
                            concatLatestFrom(() => this.store.select(selectJobs)),
                            map(([jobs, currentJobs]) => {
                                if (currentJobs.length === 0)
                                    return jobActions.loadingJobsSucceeded({
                                        jobs: jobs,
                                        updatedJobs: [],
                                    })
                                const updatedAndNewJobs = jobs.filter(job => {
                                    const currentJob = currentJobs.find(j => j.id === job.id)
                                    return (
                                        (!currentJob || currentJob.status !== job.status) &&
                                        job.status == JobStatus.Closed
                                    )
                                })
                                return jobActions.loadingJobsSucceeded({
                                    jobs: jobs,
                                    updatedJobs: updatedAndNewJobs,
                                })
                            }),
                            catchError(error =>
                                of(
                                    jobActions.loadingJobsFailed({
                                        error,
                                        message: 'Error loading jobs for dataset.',
                                    })
                                )
                            )
                        )
                    )
                )
            )
        )
    })

    stopPollingJobs$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.selectCase, userActions.startLogout),
                map(() => (this.isPollingActive = false))
            )
        },
        { dispatch: false }
    )

    notifyOfUpdatedJobs = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(jobActions.loadingJobsSucceeded),
                tap(({ updatedJobs }) => {
                    if (updatedJobs.length === 0) return;
                    
                    const plottingJobs = updatedJobs.filter(job => job.type === PipelineType.Plotting);
                    const otherJobs = updatedJobs.filter(job => job.type !== PipelineType.Plotting);
                    
                    if (plottingJobs.length > 0) {
                        this.refreshPlottingResults();
                    }
                    
                    if (otherJobs.length > 0) {
                        this.jobUpdateDialogService.showJobUpdateDialog();
                    }
                })
            )
        },
        { dispatch: false }
    )

    private refreshPlottingResults(): void {
        this.store.select(selectCaseId).pipe(
            take(1),
            filter(caseId => !!caseId),
            switchMap(caseId => 
                this.datasetApi.getPlottingResults(caseId!).pipe(
                    map(plottingResult => {
                        return caseActions.loadingPlottingResultsSucceeded({ 
                            plotting: plottingResult 
                        });
                    }),
                    catchError(error => of(
                        caseActions.loadingPlottingResultsFailed({
                            error,
                            message: 'Error refreshing plotting results.'
                        })
                    ))
                )
            )
        ).subscribe(action => {
            this.store.dispatch(action);
        });
    }

    private loadJobs(): MonoTypeOperatorFunction<LoadJobsPipeResponse> {
        return (source: Observable<LoadJobsPipeResponse>): Observable<LoadJobsPipeResponse> =>
            source.pipe(
                switchMap(() =>
                    this.jobApiService.getJobs().pipe(
                        map(jobs =>
                            jobActions.loadingJobsSucceeded({ jobs: jobs, updatedJobs: [] })
                        ),
                        catchError(error =>
                            of(
                                jobActions.loadingJobsFailed({
                                    error,
                                    message: 'Error loading jobs for dataset.',
                                })
                            )
                        )
                    )
                )
            )
    }
}

type LoadJobsPipeResponse =
    | ({ jobs: Job[]; updatedJobs: Job[] } & ReturnType<typeof jobActions.loadingJobsSucceeded>)
    | ({ error: HttpErrorResponse; message: string } & ReturnType<
          typeof jobActions.loadingJobsFailed
      >)
